import { useEffect, useState } from 'react';
import { ApiError, api, referralCode, storeToken } from '../api/client.js';
import { AboutPage, GoogleButton, RulesModal, SUPPORT_DISCORD } from '../components/AuthExtras.js';
import { AuthLogo, AuthShell } from '../components/AuthShell.js';
import { useLocale } from '../i18n/Locale.js';
import { emailProblem, normalizeEmail } from '../emailRules.js';
import '../auth-pages.css';

/**
 * Entry pages (reference look: the town map behind one dark card).
 *
 * Unlike the reference, the site opens on SIGN IN; "Criar uma conta" swaps the
 * card to registration. Each view has its own address so links and the back
 * button work: / and /login, /register, /forgot-password,
 * /reset-password?token=… (from the e-mail) and /sobre (about the game).
 */
export type AuthView = 'login' | 'register' | 'forgot' | 'reset' | 'about';

const PATHS: Record<AuthView, string> = {
  login: '/login',
  register: '/register',
  forgot: '/forgot-password',
  reset: '/reset-password',
  about: '/sobre',
};

function viewFromPath(pathname: string): AuthView {
  const path = pathname.replace(/\/+$/, '').toLowerCase();
  if (path === '/register' || path === '/criar-conta') return 'register';
  if (path === '/forgot-password' || path === '/esqueci-a-senha') return 'forgot';
  if (path === '/reset-password') return 'reset';
  if (path === '/sobre' || path === '/about' || path === '/welcome') return 'about';
  return 'login';
}

/** Pages shown even when a session is saved (about, password reset link). */
export function isPublicAuthPath(pathname: string): boolean {
  const view = viewFromPath(pathname);
  return view === 'about' || view === 'reset';
}

function resetTokenFromUrl(): string {
  try {
    return new URLSearchParams(window.location.search).get('token') ?? '';
  } catch {
    return '';
  }
}

function go(path: string, replace = false): void {
  try {
    const search = path === PATHS.reset ? window.location.search : '';
    if (replace) window.history.replaceState(null, '', path + search);
    else window.history.pushState(null, '', path);
  } catch {
    /* sandboxed iframe: the view still changes */
  }
}

export function AuthScreen({ onReady, hasSession = false }: { onReady: () => void; hasSession?: boolean }) {
  const { t, locale, setLocale } = useLocale();
  const en = locale === 'en';
  const [view, setView] = useState<AuthView>(() => viewFromPath(window.location.pathname));
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [invite, setInvite] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [identifier, setIdentifier] = useState('');
  const [forgotDone, setForgotDone] = useState<null | 'sent' | 'no-mail'>(null);
  const [resetToken] = useState(resetTokenFromUrl);
  const [resetValid, setResetValid] = useState<boolean | null>(null);
  const [betaClosed, setBetaClosed] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [referredBy] = useState(() => referralCode());

  useEffect(() => {
    void api.health()
      .then((health) => setBetaClosed(health.beta === 'closed'))
      .catch(() => setError(en ? 'The game server is offline. Try again in a moment.' : 'O servidor do jogo está offline. Tente de novo em instantes.'));
    // Only on mount: the language switch must not re-run the health check.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onPop = () => {
      setView(viewFromPath(window.location.pathname));
      setError('');
      setNotice('');
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    if (view !== 'reset') return;
    if (!resetToken) { setResetValid(false); return; }
    void api.resetTokenValid(resetToken).then((r) => setResetValid(r.valid)).catch(() => setResetValid(false));
  }, [view, resetToken]);

  // The very first visit lands on "/": show it as /login without a history entry.
  useEffect(() => {
    if (window.location.pathname === '/' && view === 'login') go(PATHS.login, true);
  }, [view]);

  function swap(next: AuthView) {
    setView(next);
    setError('');
    setNotice('');
    setForgotDone(null);
    go(PATHS[next]);
    window.scrollTo(0, 0);
  }

  function enterGame(token: string) {
    storeToken(token);
    go('/', true);
    onReady();
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (view === 'register' && !accepted) return;
    // E-mail sign-in (16/10): every account has one.
    const typed = username.trim();
    const problem = emailProblem(typed, en);
    if (problem) {
      setError(problem);
      return;
    }
    if (password.length < 8) {
      setError(!password
        ? (en ? 'E-mail and password are required.' : 'O e-mail e a senha são obrigatórios.')
        : (en ? 'The password has at least 8 characters.' : 'A senha tem pelo menos 8 caracteres.'));
      return;
    }
    setBusy(true);
    setError('');
    try {
      const result = view === 'register'
        ? await api.register(normalizeEmail(typed), password, {
          invite: invite.trim() || undefined,
          acceptRules: accepted,
        })
        : await api.login(normalizeEmail(typed), password);
      enterGame(result.token);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (en ? 'Could not connect.' : 'Não foi possível conectar.'));
    } finally {
      setBusy(false);
    }
  }

  async function submitForgot(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const result = await api.forgotPassword(identifier.trim());
      setForgotDone(result.mail ? 'sent' : 'no-mail');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (en ? 'Could not connect.' : 'Não foi possível conectar.'));
    } finally {
      setBusy(false);
    }
  }

  async function submitReset(event: React.FormEvent) {
    event.preventDefault();
    if (password !== password2) {
      setError(en ? 'The two passwords are different.' : 'As duas senhas estão diferentes.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const result = await api.resetPassword(resetToken, password);
      enterGame(result.token);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (en ? 'Could not connect.' : 'Não foi possível conectar.'));
    } finally {
      setBusy(false);
    }
  }

  const langSwitch = (
    <div className="hz-auth-lang" role="group" aria-label={en ? 'Language' : 'Idioma'}>
      <button type="button" className={en ? 'on' : ''} onClick={() => setLocale('en')}>English</button>
      <button type="button" className={en ? '' : 'on'} onClick={() => setLocale('pt')}>Português</button>
    </div>
  );

  if (view === 'about') {
    // Already signed in (about opened from a saved session): both buttons go to the characters.
    const leaveAbout = (next: AuthView) => {
      if (!hasSession) {
        swap(next);
        return;
      }
      go('/', true);
      onReady();
    };
    return (
      <AboutPage
        langSwitch={langSwitch}
        onLogin={() => leaveAbout('login')}
        onRegister={() => leaveAbout('register')}
      />
    );
  }

  const aboutLink = (
    <p className="hz-auth-about">
      <a href={PATHS.about} onClick={(event) => { event.preventDefault(); swap('about'); }}>{en ? 'About the game' : 'Sobre o jogo'}</a>
    </p>
  );

  const googleBlock = (
    <>
      <div className="hz-auth-or"><span>{t('authOr')}</span></div>
      <GoogleButton
        label={en ? 'Continue with Google' : 'Continuar com o Google'}
        onClick={() => setNotice(en ? 'Coming soon: sign in with Google.' : 'Em breve: entrar com o Google.')}
      />
      {notice ? <p className="hz-auth-soon" role="status">{notice}</p> : null}
    </>
  );

  if (view === 'forgot') {
    return (
      <AuthShell>
        {langSwitch}
        <div className="auth-card hz-auth auth-card-enter" data-view="forgot">
          <AuthLogo />
          <h1 className="hz-auth-title">{en ? 'Forgot your password?' : 'Esqueceu sua senha?'}</h1>
          {forgotDone ? (
            <div className="hz-auth-result" role="status" data-testid="forgot-result">
              {forgotDone === 'sent' ? (
                <>
                  <p>{en
                    ? 'Done! If an account with an e-mail matches, the link arrives in a few minutes. Check the spam folder too.'
                    : 'Pronto! Se existir uma conta com e-mail cadastrado para esse dado, o link chega em alguns minutos. Confira também a caixa de spam.'}</p>
                  <p className="hz-auth-hint">{en
                    ? 'No e-mail on your account? Our support can help you on Discord.'
                    : 'Não cadastrou e-mail na conta? O suporte te ajuda pelo Discord.'}</p>
                </>
              ) : (
                <p>{en
                  ? 'Password recovery by e-mail is not active yet. Talk to us on Discord and our support will help you get your account back.'
                  : 'A recuperação de senha por e-mail ainda não está ativa. Fale com a gente no Discord que o suporte te ajuda a recuperar a conta.'}</p>
              )}
              <a className="hz-auth-discord" href={SUPPORT_DISCORD} target="_blank" rel="noreferrer">
                {en ? 'Open our Discord' : 'Abrir o Discord'}
              </a>
            </div>
          ) : (
            <>
              <p className="hz-auth-lede">{en
                ? 'Type your e-mail — or your account or one of your characters\' name — and we will send you a link to choose another password.'
                : 'Digite seu e-mail — ou o nome da sua conta ou de um dos seus personagens — e a gente manda um link para escolher outra senha.'}</p>
              <form className="auth-form hz-auth-form" onSubmit={(event) => void submitForgot(event)}>
                <label className="hz-auth-field" htmlFor="auth-identifier">
                  <span>{en ? 'E-mail, account or character name' : 'E-mail, conta ou nome do personagem'}</span>
                  <input id="auth-identifier" value={identifier} onChange={(event) => setIdentifier(event.target.value)} autoComplete="username" />
                </label>
                {error ? <p className="auth-error">{error}</p> : null}
                <button className="auth-submit hz-auth-submit" disabled={busy || !identifier.trim()} type="submit">
                  {en ? 'Send the link' : 'Enviar o link'}
                </button>
              </form>
            </>
          )}
          <p className="hz-auth-switch">
            <button type="button" onClick={() => swap('login')}>{en ? 'Back to sign in' : 'Voltar para entrar'}</button>
          </p>
          {aboutLink}
        </div>
      </AuthShell>
    );
  }

  if (view === 'reset') {
    return (
      <AuthShell>
        {langSwitch}
        <div className="auth-card hz-auth auth-card-enter" data-view="reset">
          <AuthLogo />
          <h1 className="hz-auth-title">{en ? 'Choose a new password' : 'Escolha uma nova senha'}</h1>
          {resetValid === false ? (
            <div className="hz-auth-result" role="status">
              <p>{en
                ? 'This link is invalid or has expired (links last 1 hour). Ask for a new one.'
                : 'Este link é inválido ou já expirou (os links valem por 1 hora). Peça um novo.'}</p>
              <button type="button" className="auth-submit hz-auth-submit" onClick={() => swap('forgot')}>
                {en ? 'Ask for a new link' : 'Pedir um novo link'}
              </button>
            </div>
          ) : (
            <form className="auth-form hz-auth-form" onSubmit={(event) => void submitReset(event)}>
              <label className="hz-auth-field" htmlFor="auth-pass">
                <span>{en ? 'New password' : 'Nova senha'}</span>
                <input id="auth-pass" type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="new-password" />
              </label>
              <label className="hz-auth-field" htmlFor="auth-pass2">
                <span>{en ? 'Repeat the new password' : 'Repita a nova senha'}</span>
                <input id="auth-pass2" type="password" value={password2} onChange={(event) => setPassword2(event.target.value)} autoComplete="new-password" />
              </label>
              <p className="hz-auth-hint">{en ? 'Use at least 8 characters.' : 'Use pelo menos 8 caracteres.'}</p>
              {error ? <p className="auth-error">{error}</p> : null}
              <button className="auth-submit hz-auth-submit" disabled={busy || resetValid !== true || password.length < 8} type="submit">
                {en ? 'Save and play' : 'Salvar e jogar'}
              </button>
            </form>
          )}
          <p className="hz-auth-switch">
            <button type="button" onClick={() => swap('login')}>{en ? 'Back to sign in' : 'Voltar para entrar'}</button>
          </p>
          {aboutLink}
        </div>
      </AuthShell>
    );
  }

  const register = view === 'register';
  return (
    <AuthShell>
      {langSwitch}
      <div className="auth-card hz-auth auth-card-enter" data-view={view}>
        <AuthLogo />
        <h1 className="hz-auth-title">{register ? (en ? 'Create your account' : 'Crie sua conta') : (en ? 'Welcome back' : 'Bem-vindo de volta')}</h1>
        <p className="hz-auth-lede">
          {register
            ? (en ? 'Name a character and start hunting — free, in the browser.' : 'Dê nome a um personagem e comece a caçar — de graça, no navegador.')
            : (en ? 'Your hunt is right where you left it.' : 'Sua caçada está onde você parou.')}
        </p>

        <form className="auth-form hz-auth-form" noValidate onSubmit={(event) => void submit(event)}>
          <label className="hz-auth-field" htmlFor="auth-user">
            <span>E-mail</span>
            <input
              id="auth-user"
              name="email"
              type="email"
              inputMode="email"
              value={username}
              onChange={(event) => { setUsername(event.target.value); setError(''); }}
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              maxLength={254}
              placeholder={en ? 'you@example.com' : 'voce@exemplo.com'}
            />
          </label>
          <label className="hz-auth-field" htmlFor="auth-pass">
            <span>{t('password')}</span>
            <input
              id="auth-pass"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete={register ? 'new-password' : 'current-password'}
            />
          </label>
          {register && <p className="hz-auth-hint">{en ? 'Use at least 8 characters.' : 'Use pelo menos 8 caracteres.'}</p>}
          {register && betaClosed && (
            <label className="hz-auth-field" htmlFor="auth-invite">
              <span>{t('invite')}</span>
              <input
                id="auth-invite"
                value={invite}
                onChange={(event) => setInvite(event.target.value)}
                autoComplete="off"
                placeholder="BETA-XXXX"
              />
            </label>
          )}
          {register && referredBy ? <p className="meta">{en ? 'Referred by' : 'Indicado por'} <b>{referredBy.replace(/_/g, ' ')}</b></p> : null}
          {register && (
            <label className="hz-auth-rules" htmlFor="auth-rules">
              <input
                id="auth-rules"
                type="checkbox"
                checked={accepted}
                onChange={(event) => setAccepted(event.target.checked)}
                data-testid="accept-rules"
              />
              <span>
                {en ? 'I have read and accept the rules of Kazbia Online.' : 'Li e aceito as regras do Kazbia Online.'}{' '}
                <button type="button" className="hz-auth-link" onClick={() => setRulesOpen(true)} data-testid="read-rules">
                  {en ? 'Read the rules' : 'Ler as regras'}
                </button>
              </span>
            </label>
          )}
          {error ? <p className="auth-error">{error}</p> : null}
          <button className="auth-submit hz-auth-submit" disabled={busy || (register && !accepted)} type="submit">
            {register ? t('createAccount') : t('signIn')}
          </button>
        </form>

        {googleBlock}

        <p className="hz-auth-switch">
          {register
            ? <>{en ? 'Already playing?' : 'Já joga?'} <button type="button" onClick={() => swap('login')} data-testid="to-login">{t('signIn')}</button></>
            : <>{en ? 'New here?' : 'Novo por aqui?'} <button type="button" onClick={() => swap('register')} data-testid="to-register">{en ? 'Create an account' : 'Criar uma conta'}</button></>}
        </p>
        {!register && (
          <p className="hz-auth-forgot">
            <button type="button" onClick={() => swap('forgot')} data-testid="to-forgot">{en ? 'Forgot your password?' : 'Esqueceu sua senha?'}</button>
          </p>
        )}
        {aboutLink}
      </div>
      {rulesOpen && (
        <RulesModal
          onClose={() => setRulesOpen(false)}
          onAccept={() => {
            setAccepted(true);
            setRulesOpen(false);
          }}
        />
      )}
    </AuthShell>
  );
}
